import React, { useEffect, useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Button,
    TextField,
    Card,
    CardContent,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    Snackbar,
    Collapse,
    LinearProgress,
} from '@material-ui/core';
import { ArrowBack, Link, GetApp, Edit, Search, ExpandMore } from '@material-ui/icons';
import AlertFreemiumPlan from '../AlertFreemiumPlan';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { getStoreService, getUserStoresService } from '../../services/store';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { errorToast, successToast } from '../../constants.js/misc';
import { Link as RouterLink } from 'react-router-dom';
import { setUserStoreToEdit } from '../../modules/user-stores';
import QRCode from 'qrcode';

const MobileUserStoresTablePage = ({ storeId }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const [search, setSearch] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '' });
    const [accordionOpen, setAccordionOpen] = useState(true);
    const [mainBranch, setMainBranch] = useState('');
    const [storeData, setStoreData] = useState({});
    const [gettingData, setGettingData] = useState(true);
    const [branches, setBranches] = useState([]);
    const [page, setPage] = useState(0);
    const [limit] = useState(10); // You can adjust per page
    const [totalCount, setTotalCount] = useState(0);
    const [downloadQR, setDownloadQR] = useState([]);
    const [storeReady, setStoreReady] = useState(false);
    const [listReady, setListReady] = useState(false);

    const isFreemiumPlan = storeData?.store_plan?.plan?.name?.toLowerCase() === 'freemium';
    const maxLines = Number(storeData?.plan_features?.max_lines);
    const sucursalCount = Math.max(totalCount, branches.length);
    const atLineLimit = maxLines > 0 && sucursalCount >= maxLines;
    const isCreateDisabled = isFreemiumPlan || !storeReady || !listReady || atLineLimit;

    const filteredBranches = (branches || []).filter((b) =>
        (b.mobile_phone_number || '').toLowerCase().includes(search.toLowerCase())
    );

    const showToast = (message) => {
        setToast({ open: true, message });
        setTimeout(() => setToast({ open: false, message: '' }), 3000);
    };

    const copyLink = (phone) => {
        document.addEventListener('copy', (e) => {
            e.clipboardData.setData(
                'text/plain',
                `https://${
                    storeData.domain ? storeData.domain : `${storeData.url}.ecometri.shop`
                }/${phone}`
            );
            e.preventDefault();
            document.removeEventListener('copy', null);
        });
        document.execCommand('copy');
        enqueueSnackbar(t('UserStores.copied'), successToast);
    };

    const handleDownloadQR = async (phone) => {
        enqueueSnackbar('Descargando código QR...', successToast);

        const y = `https://${
            storeData.domain ? storeData.domain : `${storeData.url}.ecometri.shop`
        }/${phone}`;
        const res = await QRCode.toDataURL(y, { errorCorrectionLevel: 'H' });

        // Create a temporary link for download
        const link = document.createElement('a');
        link.href = res;
        link.download = `qr_${phone}.png`; // 👈 sets file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        getStoreService(storeId)
            .then((res) => {
                setStoreData(res.data);
            })
            .finally(() => {
                setStoreReady(true);
            });
    }, []);

    useEffect(() => {
        setGettingData(true);
        getUserStoresService(storeId, limit, page)
            .then((res) => {
                const newBranches = res.data || [];
                const total = parseInt(res.headers['x-total-count'] || '0', 10);

                // If page === 1, replace; otherwise append
                setBranches((prev) => (page === 1 ? newBranches : [...prev, ...newBranches]));
                setTotalCount(total);
                setListReady(true);
                setGettingData(false);
            })
            .catch((err) => {
                console.log('Error UserStoresTablePage', err);
                setListReady(true);
                setGettingData(false);
                enqueueSnackbar(t('UserStores.tableError'), errorToast);
            });
    }, [storeId, page]);

    // Auto-select first branch if plan is freemium
    useEffect(() => {
        const planName = storeData.store_plan?.plan?.name?.toLowerCase();
        if (planName === 'freemium' && branches.length > 0) {
            setMainBranch(branches[0].name);
        }
    }, [storeData, branches]);

    return (
        <div style={{ backgroundColor: '#f9fafb', minHeight: '100vh', padding: '16px 0 48px' }}>
            <div>
                <Toolbar>
                    <IconButton edge="start" onClick={() => window.history.back()}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" style={{ flexGrow: 1 }}>
                        Sucursales
                    </Typography>
                </Toolbar>
            </div>

            <div style={{ padding: 16 }}>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                    Administra las líneas de WhatsApp donde vendes productos, envías y recibes
                    pedidos.
                </Typography>

                {storeData?.store_plan?.plan?.name?.toLowerCase() === 'freemium' && (
                    <div style={{ margin: '16px 0' }}>
                        <AlertFreemiumPlan alertMessage="Plan Freemium: incluye 1 sucursal. ¿Necesitas más? Mejora tu plan." />
                    </div>
                )}

                {/* Create new branch */}
                <Tooltip
                    title={t('UserStores.createLimitReached', { count: maxLines })}
                    placement="top"
                    arrow
                    disableHoverListener={!atLineLimit || isFreemiumPlan}
                >
                    <span style={{ width: '100%', display: 'block' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isCreateDisabled}
                            onClick={() => setOpenModal(true)}
                        >
                            {isFreemiumPlan && <span>👑 + </span>}
                            Crear una nueva sucursal WhatsApp
                        </Button>
                    </span>
                </Tooltip>

                {/* Search */}
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 24 }}>
                    <Search color="action" />
                    <TextField
                        placeholder="Buscar por número de WhatsApp"
                        variant="outlined"
                        size="small"
                        fullWidth
                        style={{ marginLeft: 8 }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                {/* Loading indicator */}
                {gettingData && (
                    <div style={{ marginTop: 24 }}>
                        <LinearProgress />
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            align="center"
                            style={{ marginTop: 8 }}
                        >
                            Cargando sucursales...
                        </Typography>
                    </div>
                )}

                {/* Branch list */}
                {!gettingData && (
                    <div style={{ marginTop: 24 }}>
                        {filteredBranches.length > 0 ? (
                            filteredBranches.map((branch) => (
                                <Card key={branch.id} style={{ marginBottom: 12 }}>
                                    <CardContent
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div>
                                            <Typography variant="subtitle1">
                                                {branch.name}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {branch.mobile_phone_number}
                                            </Typography>
                                        </div>
                                        <div>
                                            <IconButton
                                                color="default"
                                                onClick={() => copyLink(branch.mobile_phone_number)}
                                            >
                                                <Link fontSize="small" />
                                            </IconButton>

                                            <IconButton
                                                color="default"
                                                onClick={() =>
                                                    handleDownloadQR(branch.mobile_phone_number)
                                                }
                                            >
                                                <GetApp fontSize="small" />
                                            </IconButton>

                                            <IconButton
                                                color="default"
                                                component={RouterLink}
                                                to={`/user-stores/update/${branch.id}`}
                                                onClick={() => setUserStoreToEdit(branch)}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))
                        ) : (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                                style={{ marginTop: 16 }}
                            >
                                No se encontraron sucursales.
                            </Typography>
                        )}
                    </div>
                )}

                {/* Load more */}
                {!gettingData && (
                    <Button
                        variant="outlined"
                        fullWidth
                        style={{ marginTop: 8 }}
                        disabled={branches.length >= totalCount || gettingData}
                        onClick={() => setPage((prev) => prev + 1)}
                    >
                        {branches.length >= totalCount
                            ? 'No hay más sucursales'
                            : gettingData
                            ? 'Cargando...'
                            : 'Cargar más sucursales'}
                    </Button>
                )}

                {/* Main branch (accordion) */}
                <Card style={{ marginTop: 24 }}>
                    <CardContent>
                        <Button
                            fullWidth
                            onClick={() => setAccordionOpen(!accordionOpen)}
                            style={{ justifyContent: 'space-between' }}
                        >
                            <Typography variant="subtitle1">Sucursal principal</Typography>
                            <ExpandMore
                                style={{
                                    transform: accordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                }}
                            />
                        </Button>

                        <Collapse in={accordionOpen}>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ margin: '12px 0' }}
                            >
                                Gestiona la sucursal WhatsApp que la tienda usará cuando no se
                                especifique otra.
                            </Typography>
                            <Select
                                fullWidth
                                variant="outlined"
                                value={mainBranch}
                                onChange={(e) => setMainBranch(e.target.value)}
                                disabled={
                                    storeData.store_plan?.plan?.name?.toLowerCase() === 'freemium'
                                }
                            >
                                <MenuItem value="">Seleccionar sucursal</MenuItem>
                                {branches.map((b) => (
                                    <MenuItem key={b.id} value={b.name}>
                                        {b.name} - {b.mobile_phone_number}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                style={{ marginTop: 12 }}
                                onClick={() => showToast('Sucursal principal actualizada')}
                                disabled={
                                    storeData.store_plan?.plan?.name?.toLowerCase() === 'freemium'
                                }
                            >
                                Establecer sucursal principal
                            </Button>
                        </Collapse>
                    </CardContent>
                </Card>
            </div>

            {/* Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="xs">
                <DialogTitle>Nueva sucursal WhatsApp</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Nombre de la sucursal *"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        placeholder="Ej. Sucursal Centro"
                    />
                    <TextField
                        label="Número de la línea de WhatsApp *"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        placeholder="+52 55 1234 5678"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={() => {
                            setOpenModal(false);
                            showToast('Sucursal guardada exitosamente');
                        }}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast */}
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                open={toast.open}
                message={toast.message}
            />
        </div>
    );
};

const mapStateToProps = ({ auth, userStores }) => ({
    storeId: auth.storeId,
    userStoreToEdit: userStores.userStoreToEdit,
    initialRowsPerPage: userStores.rowsPerPage,
    initialPage: userStores.page,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ setUserStoreToEdit }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MobileUserStoresTablePage);
